import React, { useState, useEffect } from 'react';
import DynamicBackground from '../../components/DynamicBackground';
import CreditsDisplay from '../../components/CreditsDisplay';
import ExpertContactMessage from '../../components/ExpertContactMessage';
import '../../styles/darkMode.css';
import './interpreter.css';
import { uploadFile, getContractTypes, ContractTypeInfo } from '../../services/pdf.service';
import { useUser } from '../../contexts/UserContext';
import { parseResponse, ContractAnalysis } from '../../services/contract-parser.service';
import { deductCredit } from '../../services/user.service';
import { generatePDF } from '../../utils/pdfGenerator';
import ChatInterface from '../../components/ChatInterface';
import StarRating from '../../components/StarRating/StarRating';

interface ApiResponse {
  content?: string;
  error?: string;
}

interface GroupedContracts {
  [category: string]: {
    [subCategory: string]: ContractTypeInfo[];
  };
}


const DocumentInterpreter: React.FC = () => {
  const { refreshUser, user } = useUser();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [apiResponse, setApiResponse] = useState<ApiResponse>({});
  const [isLoading, setIsLoading] = useState(false);
  const [selectedContractId, setSelectedContractId] = useState<string>('');
  const [showUploadSection, setShowUploadSection] = useState(true);
  const [analysis, setAnalysis] = useState<ContractAnalysis>({});
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [currentAnalysisId, setCurrentAnalysisId] = useState<string>('');
  
  // Contract types from API
  const [contractTypes, setContractTypes] = useState<ContractTypeInfo[]>([]);
  const [groupedContracts, setGroupedContracts] = useState<GroupedContracts>({});
  const [isLoadingContracts, setIsLoadingContracts] = useState(true);
  const [contractsError, setContractsError] = useState<string | null>(null);
  
  // Wizard step state
  type WizardStep = 'select' | 'upload';
  const [wizardStep, setWizardStep] = useState<WizardStep>('select');

  // Drill-down navigation state
  type DrillStep = 'categories' | 'subcategories' | 'contracts';
  const [drillStep, setDrillStep] = useState<DrillStep>('categories');
  const [drillCategory, setDrillCategory] = useState<string>('');
  const [drillSubcategory, setDrillSubcategory] = useState<string>('');
  
  // Search state
  const [searchQuery, setSearchQuery] = useState<string>('');

  const [showChat, setShowChat] = useState(false);

  // Fetch contract types on mount
  useEffect(() => {
    const fetchContractTypes = async () => {
      try {
        setIsLoadingContracts(true);
        setContractsError(null);
        const types = await getContractTypes();
        setContractTypes(types);
        
        // Group contracts by category, then subcategory
        // Normalize category names (case-insensitive) to prevent duplicates
        const grouped: GroupedContracts = {};
        const categoryKeyMap = new Map<string, string>(); // Maps lowercase -> first occurrence (preserves casing)
        
        types.forEach((contract) => {
          // Normalize: trim whitespace
          const rawCategory = (contract.category || 'Other').trim();
          const rawSubCategory = (contract.subCategory || 'General').trim();
          
          // Get normalized category key (case-insensitive)
          const categoryKey = rawCategory.toLowerCase();
          let normalizedCategory = categoryKeyMap.get(categoryKey);
          
          if (!normalizedCategory) {
            // First time seeing this category (case-insensitive), use original casing
            normalizedCategory = rawCategory;
            categoryKeyMap.set(categoryKey, normalizedCategory);
          } else {
            // Use the first occurrence's casing
            normalizedCategory = categoryKeyMap.get(categoryKey)!;
          }
          
          if (!grouped[normalizedCategory]) {
            grouped[normalizedCategory] = {};
          }
          
          // Normalize subcategory similarly
          const normalizedSubCategory = rawSubCategory;
          
          // For subcategories, we can keep them as-is since they're nested under categories
          if (!grouped[normalizedCategory][normalizedSubCategory]) {
            grouped[normalizedCategory][normalizedSubCategory] = [];
          }
          grouped[normalizedCategory][normalizedSubCategory].push(contract);
        });
        setGroupedContracts(grouped);
        
        // All categories and subcategories start collapsed (minimized) by default
        // Users can click to expand them
        
        // Set default selection to first contract if available
        if (types.length > 0) {
          setSelectedContractId(types[0].id);
        }
      } catch (error) {
        console.error('Failed to fetch contract types:', error);
        setContractsError('Failed to load contract types. Please refresh the page.');
      } finally {
        setIsLoadingContracts(false);
      }
    };

    fetchContractTypes();
  }, []);

  // Filter contracts based on search query
  const filterContracts = React.useCallback((contract: ContractTypeInfo): boolean => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      contract.nameToUse?.toLowerCase().includes(query) ||
      contract.description?.toLowerCase().includes(query) ||
      contract.category?.toLowerCase().includes(query) ||
      contract.subCategory?.toLowerCase().includes(query) ||
      contract.internalName?.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  // Filter and group contracts based on search - computed at component level
  const filteredGroupedContracts = React.useMemo((): GroupedContracts => {
    if (!searchQuery.trim()) {
      return groupedContracts;
    }

    const filtered: GroupedContracts = {};
    Object.entries(groupedContracts).forEach(([category, subCategories]) => {
      const filteredSubCategories: { [key: string]: ContractTypeInfo[] } = {};
      
      Object.entries(subCategories).forEach(([subCategory, contracts]) => {
        const filteredContracts = contracts.filter(filterContracts);
        if (filteredContracts.length > 0) {
          filteredSubCategories[subCategory] = filteredContracts;
        }
      });

      if (Object.keys(filteredSubCategories).length > 0) {
        filtered[category] = filteredSubCategories;
      }
    });

    return filtered;
  }, [searchQuery, groupedContracts, filterContracts]);

  // Reset drill to root when search is cleared
  useEffect(() => {
    if (!searchQuery.trim()) {
      setDrillStep('categories');
      setDrillCategory('');
      setDrillSubcategory('');
    }
  }, [searchQuery]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      const allowedTypes = [
        'application/pdf',
        'text/plain',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'image/jpeg',
        'image/png',
      ];
      
      if (!allowedTypes.includes(file.type)) {
        setApiResponse({ error: 'Please upload a PDF, TXT, DOCX, or image file.' });
        return;
      }
      setSelectedFile(file);
      setApiResponse({}); // Clear previous response
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setApiResponse({ error: 'Please select a file.' });
      return;
    }

    if (!selectedContractId) {
      setApiResponse({ error: 'Please select a contract type.' });
      return;
    }

    setIsLoading(true);
    setApiResponse({}); // Clear previous response

    try {
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      if (!storedUser.id) {
        throw new Error('User not authenticated');
      }

      // Find the selected contract to get its internal name
      const selectedContract = contractTypes.find(t => t.id === selectedContractId);
      if (!selectedContract) {
        throw new Error('Selected contract type not found');
      }

      // Deduct credit before processing
      console.log('Deducting credit...');
      try {
        await deductCredit(storedUser.id);
        console.log('Credit deducted successfully');
      } catch (creditError) {
        console.error('Credit deduction failed:', creditError);
        throw new Error(creditError instanceof Error ? creditError.message : 'Failed to deduct credit');
      }

      const { content, analysisId } = await uploadFile(selectedContract.internalName, selectedFile);
      setApiResponse({ content });
      setAnalysis(parseResponse(content));
      setCurrentAnalysisId(analysisId);
      setShowUploadSection(false);
      setShowChat(false);
      
      // Refresh user credits after successful analysis
      await refreshUser();
    } catch (error) {
      console.error('Error processing file:', error);
      setApiResponse({ error: error instanceof Error ? error.message : 'An error occurred while processing the file. Please try again.' });
      // Refresh user to ensure credits are in sync
      await refreshUser();
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setApiResponse({});
    setAnalysis({});
    setSelectedContractId('');
    setShowUploadSection(true);
    setDisclaimerAccepted(false);
    setShowChat(false);
    setWizardStep('select');
    setDrillStep('categories');
    setDrillCategory('');
    setDrillSubcategory('');
    setSearchQuery('');
    setCurrentAnalysisId('');
  };

  const handleDownloadPDF = async () => {
    if (isGeneratingPDF) return;
    setIsGeneratingPDF(true);
    try {
      // Use a timestamp for the filename
      const fileName = `contract-analysis-${new Date().toISOString().split('T')[0]}`;
      await generatePDF('analysis-content', fileName);
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      // You could add a toast notification here
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const renderIssues = (issues: any[] | undefined, className: string) => {
    if (!issues || issues.length === 0) return null;
    
    const renderFormattedText = (text: string) => {
      if (typeof text !== 'string') return null;
      
      // Split the text by **bold** pattern
      const parts = text.split(/(\*\*.*?\*\*)/g);
      
      return parts.map((part, index) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          // Remove the stars and wrap in strong tag
          return <strong key={index}>{part.slice(2, -2)}</strong>;
        }
        return part;
      });
    };
    
    return (
      <ul className={`issues-list ${className}`}>
        {issues.map((issue, index) => {
          if (typeof issue === 'string') {
            return (
              <li key={index}>
                {renderFormattedText(issue)}
              </li>
            );
          } else {
            // It's a structured Issue object
            return (
              <li key={index} className="structured-issue">
                <div className="issue-item">
                  <strong>{issue.item}</strong>
                </div>
                <div className="issue-explanation">
                  {renderFormattedText(issue.explanation)}
                </div>
                {(issue.Fix || issue.fix) && (
                  <div className="issue-fix">
                    <span className="fix-label">Suggested Fix:</span> {issue.Fix || issue.fix}
                  </div>
                )}
              </li>
            );
          }
        })}
      </ul>
    );
  };

  const renderTier1Fundamentals = () => {
    if (!analysis.tier1Fundamentals || analysis.tier1Fundamentals.length === 0) return null;

    return (
      <div className="analysis-section fundamentals-section">
        <h4>Tier-1 Fundamentals</h4>
        <div className="fundamentals-grid">
          {analysis.tier1Fundamentals.map((fundamental, index) => (
            <div key={index} className="fundamental-card">
              <div className="fundamental-heading">{fundamental.heading}</div>
              <div className="fundamental-observation">{fundamental.observation}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderStructuredResponse = () => (
    <>
      {analysis.summary && (
        <div className="analysis-section summary-section">
          <h4>Summary</h4>
          <p>{analysis.summary}</p>
        </div>
      )}

      {renderTier1Fundamentals()}

      {analysis.redIssues && analysis.redIssues.length > 0 && (
        <div className="analysis-section red-section">
          <h4>Highly Problematic Issues</h4>
          {renderIssues(analysis.redIssues, 'red-issues')}
        </div>
      )}

      {analysis.yellowIssues && analysis.yellowIssues.length > 0 && (
        <div className="analysis-section yellow-section">
          <h4>Potential Long-Term Problems</h4>
          {renderIssues(analysis.yellowIssues, 'yellow-issues')}
        </div>
      )}

      {analysis.greenIssues && analysis.greenIssues.length > 0 && (
        <div className="analysis-section green-section">
          <h4>Negotiable, Non-Critical Points</h4>
          {renderIssues(analysis.greenIssues, 'green-issues')}
        </div>
      )}

      {analysis.overallEvaluation && (
        <div className="analysis-section evaluation-section">
          <h4>Overall Evaluation</h4>
          <p>{analysis.overallEvaluation}</p>
        </div>
      )}

      {analysis.remainingUncertainties && analysis.remainingUncertainties.length > 0 && (
        <div className="analysis-section uncertainties-section">
          <h4>Remaining Uncertainties</h4>
          {renderIssues(analysis.remainingUncertainties, 'uncertainties')}
        </div>
      )}
    </>
  );

  const renderUnstructuredResponse = () => (
    <div className="analysis-section unstructured-section">
      <h4>Analysis Result</h4>
      <div className="unstructured-content">
        {analysis.summary}
      </div>
    </div>
  );

  const selectedContractType = contractTypes.find(type => type.id === selectedContractId);

  // Full-width contract type selection (wizard step 1)
  const renderTypeSelection = () => {
    if (isLoadingContracts) {
      return (
        <div className="type-selection-container">
          <div className="loading-contracts">
            <span className="loading-spinner"></span>
            <p>Loading contract types...</p>
          </div>
        </div>
      );
    }

    if (contractsError) {
      return (
        <div className="type-selection-container">
          <div className="contracts-error">
            <p>{contractsError}</p>
            <button onClick={() => window.location.reload()} className="dark-button secondary">
              Retry
            </button>
          </div>
        </div>
      );
    }

    const sortedCategoryKeys = (obj: GroupedContracts) =>
      Object.keys(obj).sort((a, b) => {
        if (a === 'Other') return 1;
        if (b === 'Other') return -1;
        return a.localeCompare(b);
      });

    const handleCategoryClick = (category: string) => {
      const subCats = groupedContracts[category] || {};
      const subCatKeys = Object.keys(subCats);
      const skipSubcategories =
        subCatKeys.length <= 1 ||
        (subCatKeys.length === 1 && (subCatKeys[0] === 'General' || subCatKeys[0] === ''));
      setDrillCategory(category);
      if (skipSubcategories) {
        setDrillSubcategory(subCatKeys[0] || 'General');
        setDrillStep('contracts');
      } else {
        setDrillStep('subcategories');
      }
    };

    const handleSubcategoryClick = (subcat: string) => {
      setDrillSubcategory(subcat);
      setDrillStep('contracts');
    };

    const handleBack = () => {
      if (drillStep === 'subcategories') {
        setDrillStep('categories');
        setDrillCategory('');
      } else if (drillStep === 'contracts') {
        const subCats = groupedContracts[drillCategory] || {};
        const subCatKeys = Object.keys(subCats);
        const hadSubcategoryStep =
          subCatKeys.length > 1 &&
          !(subCatKeys.length === 1 && (subCatKeys[0] === 'General' || subCatKeys[0] === ''));
        if (hadSubcategoryStep) {
          setDrillStep('subcategories');
          setDrillSubcategory('');
        } else {
          setDrillStep('categories');
          setDrillCategory('');
          setDrillSubcategory('');
        }
      }
    };

    const selectContract = (id: string) => {
      setSelectedContractId(id);
      setWizardStep('upload');
    };

    const renderSearchResults = () => {
      const hasResults = Object.keys(filteredGroupedContracts).length > 0;
      if (!hasResults) {
        return (
          <div className="no-search-results">
            <p>No contract types found matching "{searchQuery}"</p>
            <button className="dark-button secondary" onClick={() => setSearchQuery('')}>
              Clear Search
            </button>
          </div>
        );
      }
      return (
        <div className="type-selection-grid">
          {sortedCategoryKeys(filteredGroupedContracts).map((category) =>
            Object.entries(filteredGroupedContracts[category]).map(([subCat, contracts]) =>
              contracts.map((contract) => (
                <div
                  key={contract.id}
                  className="selection-contract-card"
                  onClick={() => selectContract(contract.id)}
                >
                  <h4 className="selection-contract-name">{contract.nameToUse || contract.internalName}</h4>
                  <span className="search-result-meta">
                    {category}{subCat !== 'General' && subCat !== '' ? ` · ${subCat}` : ''}
                  </span>
                </div>
              ))
            )
          )}
        </div>
      );
    };

    const renderCategoryStep = () => (
      <div className="type-selection-grid">
        {sortedCategoryKeys(groupedContracts).map((category) => {
          const subCats = groupedContracts[category];
          const totalContracts = Object.values(subCats).reduce((sum, arr) => sum + arr.length, 0);
          return (
            <div
              key={category}
              className="drill-category-item"
              onClick={() => handleCategoryClick(category)}
            >
              <span className="drill-item-label">{category}</span>
              <div className="drill-item-right">
                <span className="drill-item-count">{totalContracts}</span>
                <span className="drill-chevron">›</span>
              </div>
            </div>
          );
        })}
      </div>
    );

    const renderSubcategoryStep = () => {
      const subCats = groupedContracts[drillCategory] || {};
      return (
        <>
          <div className="drill-nav">
            <button className="drill-back-btn" onClick={handleBack}>‹ Back</button>
            <span className="drill-breadcrumb">{drillCategory}</span>
          </div>
          <div className="type-selection-grid">
            {Object.keys(subCats)
              .sort((a, b) => a.localeCompare(b))
              .map((subCat) => {
                const contracts = subCats[subCat];
                return (
                  <div
                    key={subCat}
                    className="drill-category-item"
                    onClick={() => handleSubcategoryClick(subCat)}
                  >
                    <span className="drill-item-label">{subCat}</span>
                    <div className="drill-item-right">
                      <span className="drill-item-count">{contracts.length}</span>
                      <span className="drill-chevron">›</span>
                    </div>
                  </div>
                );
              })}
          </div>
        </>
      );
    };

    const renderContractsStep = () => {
      const contracts = groupedContracts[drillCategory]?.[drillSubcategory] || [];
      return (
        <>
          <div className="drill-nav">
            <button className="drill-back-btn" onClick={handleBack}>‹ Back</button>
            <span className="drill-breadcrumb">
              {drillCategory}
              {drillSubcategory && drillSubcategory !== 'General' && drillSubcategory !== ''
                ? ` › ${drillSubcategory}`
                : ''}
            </span>
          </div>
          <div className="type-selection-grid">
            {contracts.map((contract) => (
              <div
                key={contract.id}
                className="selection-contract-card"
                onClick={() => selectContract(contract.id)}
              >
                <h4 className="selection-contract-name">{contract.nameToUse || contract.internalName}</h4>
                {contract.description && (
                  <p className="selection-contract-desc">{contract.description}</p>
                )}
              </div>
            ))}
          </div>
        </>
      );
    };

    return (
      <div className="type-selection-container">
        <div className="contract-search-wrapper">
          <input
            type="text"
            className="contract-search-input"
            placeholder="Search all contract types..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              className="contract-search-clear"
              onClick={() => setSearchQuery('')}
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>

        {searchQuery.trim() ? (
          renderSearchResults()
        ) : (
          <>
            {drillStep === 'categories' && renderCategoryStep()}
            {drillStep === 'subcategories' && renderSubcategoryStep()}
            {drillStep === 'contracts' && renderContractsStep()}
          </>
        )}
      </div>
    );
  };

  return (
    <DynamicBackground>
      <div className="interpreter-layout">
        <div className="interpreter-main">
          <div className="dark-container">
            <div className="interpreter-header">
              <CreditsDisplay variant="header" />
              <h2 className="dark-title">Contract Interpreter</h2>
              <p className="dark-subtitle">Upload your contract and get a detailed analysis of its terms and conditions</p>
            </div>

            {showUploadSection && (
              <div className="wizard-steps">
                <div className={`wizard-step ${wizardStep === 'select' ? 'active' : 'done'}`}>
                  <span className="wizard-step-num">{wizardStep === 'select' ? '1' : '✓'}</span>
                  <span className="wizard-step-label">Select Contract Type</span>
                </div>
                <div className="wizard-step-connector" />
                <div className={`wizard-step ${wizardStep === 'upload' ? 'active' : ''}`}>
                  <span className="wizard-step-num">2</span>
                  <span className="wizard-step-label">Upload & Analyze</span>
                </div>
              </div>
            )}

            {showUploadSection ? (
              wizardStep === 'select' ? (
                renderTypeSelection()
              ) : (
                <div className="upload-section">
                  <div className="selected-contract-type">
                    <button
                      className="wizard-change-btn"
                      onClick={() => setWizardStep('select')}
                    >
                      ‹ Change Contract Type
                    </button>
                    <div className="contract-type-display">
                      <span className="contract-type-name">
                        {selectedContractType?.nameToUse || selectedContractType?.internalName}
                      </span>
                    </div>
                    {selectedContractType?.description && (
                      <div className="contract-description-main">
                        <p className="description-text-main">{selectedContractType.description}</p>
                      </div>
                    )}
                  </div>
                  <div className="file-input-wrapper">
                    <input
                      type="file"
                      id="file-input"
                      className="file-input"
                      accept=".pdf,.txt,.docx,.doc,.jpg,.jpeg,.png,.gif"
                      onChange={handleFileChange}
                    />
                    <label htmlFor="file-input" className="file-input-label">
                      Choose File (PDF, TXT, DOCX, or image)
                    </label>
                  </div>

                  {selectedFile && (
                    <div className="selected-file dark-text">
                      Selected file: {selectedFile.name}
                    </div>
                  )}

                  <div className="disclaimer-section">
                    <label className="disclaimer-checkbox">
                      <input
                        type="checkbox"
                        checked={disclaimerAccepted}
                        onChange={(e) => setDisclaimerAccepted(e.target.checked)}
                      />
                      <span className="disclaimer-text dark-text">
                        I understand that this contract interpretation is provided by AI and is not legal advice.
                        This analysis is for informational purposes only and should not be relied upon as a substitute
                        for professional legal advice. I should consult with a qualified attorney for legal guidance
                        regarding any contract or legal matter.
                      </span>
                    </label>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'stretch' }}>
                    <button
                      className="dark-button primary upload-button"
                      onClick={handleUpload}
                      disabled={isLoading || !selectedFile || !disclaimerAccepted || !selectedContractId || (user?.credits === 0 && user?.subscriptionStatus !== 'active')}
                    >
                      {isLoading ? (
                        <>
                          <span className="loading-spinner"></span>
                          Processing...
                        </>
                      ) : (
                        'Analyze Contract'
                      )}
                    </button>
                    <p className="dark-text" style={{ fontSize: '0.875rem', margin: 0, opacity: 0.8, textAlign: 'center' }}>
                      💎 This service costs 1 credit
                      {user?.subscriptionStatus === 'active' && ' (Unlimited for subscribers)'}
                    </p>
                  </div>
                </div>
              )
            ) : (
              <div className="response-section">
                <div id="analysis-content">
                  <h3 className="dark-text">Contract Analysis</h3>
                  {analysis.isStructured ? renderStructuredResponse() : renderUnstructuredResponse()}
                </div>

                <div className="button-group">
                  <button className="dark-button secondary" onClick={handleReset}>
                    Upload Another Document
                  </button>
                  <button
                    className="dark-button primary"
                    onClick={handleDownloadPDF}
                    disabled={isGeneratingPDF}
                  >
                    {isGeneratingPDF ? (
                      <>
                        <span className="loading-spinner" style={{ width: '16px', height: '16px', borderWidth: '2px' }}></span>
                        Generating PDF...
                      </>
                    ) : (
                      'Download Analysis'
                    )}
                  </button>
                  <button
                    className="dark-button primary"
                    onClick={() => window.location.href = '/dashboard'}
                  >
                    View Analysis History
                  </button>
                </div>

                {apiResponse.content && (
                  !showChat ? (
                    <button
                      className="chat-cta-button"
                      onClick={() => setShowChat(true)}
                    >
                      <span className="chat-cta-icon">💬</span>
                      <span className="chat-cta-text">
                        <strong>Ask questions about this contract</strong>
                        <small>Chat with AI about specific clauses, terms, or concerns</small>
                      </span>
                      <span className="chat-cta-arrow">›</span>
                    </button>
                  ) : (
                    <ChatInterface
                      context={apiResponse.content}
                      title="Chat with your Contract Assistant"
                    />
                  )
                )}

                {currentAnalysisId && (
                  <StarRating analysisId={currentAnalysisId} />
                )}
              </div>
            )}

            {apiResponse.error && (
              <div className="error-message">
                <h3 className="dark-text">Error</h3>
                <p className="dark-text-muted">{apiResponse.error}</p>
              </div>
            )}

            <ExpertContactMessage />
          </div>
        </div>
      </div>
    </DynamicBackground>
  );
};

export default DocumentInterpreter;
